/** Words for a source applied as ordered parts: one review, one apply, one result. */
import type { ReceiptAction } from "@/lib/api/imports";
import type { Verdict } from "@/lib/import-parts";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const part = (index: number, count: number) => `Part ${index + 1} of ${count}`;
const VERDICT: Record<ReceiptAction, string> = {
  would_create: "would create", created: "created", matched: "matched", skipped: "left out", rejected: "refused", corrected: "corrected",
};
const verdict = (v: Verdict | null) => v ? VERDICT[v.action] + (v.reason ? ` (${v.reason})` : "") : "absent";

export const importPartsWords = {
  parts: "Parts",
  partsNotRead: "Not read",
  reviewed: "Reviewed",
  applied: "Applied",
  runRecord: "Run record",
  everyPartApplied: "Every part of this source is applied.",
  continueNote: "Applied parts are never sent again. Each later part reads a fresh dry run first and stops if anything else changed.",
  freshDryRunCouldNotBeRead: "Its fresh dry run could not be read. Nothing in it was applied.",
  readTheDryRunAgain: "Its records changed since your review. Read the dry run again, then continue.",
  continueToApply: "Continue to apply it and the parts after it.",
};

export const importPartsText = {
  part,
  /** "Part 2 of 3 · 98 rows" */
  partRows: (index: number, count: number, rows: number) => `${part(index, count)} · ${plural(rows, "row", "rows")}`,
  sourceInParts: (rows: number, count: number) => `A batch holds at most 100 rows, so these ${rows} rows are applied as ${count} parts in order.`,
  partProblem: (key: string, rows: number) => `The ${key.slice(0, key.indexOf(":"))} row "${key.slice(key.indexOf(":") + 1)}" needs ${rows} rows with its parents, and a part holds at most 100. Split this source before applying.`,
  dryRunOfEveryPart: (count: number) => `Dry run of all ${count} parts`,
  dryRunOfPartsLeft: (count: number) => `Dry run of the ${plural(count, "part", "parts")} not applied`,
  counts: (create: number, matched: number, leftOut: number, refused: number) => `${create} would create · ${matched} matched · ${leftOut} left out · ${refused} refused`,
  repeatedOnce: (n: number) => `${plural(n, "record appears", "records appear")} in more than one part, because a later part needs ${n === 1 ? "it" : "them"} as a parent. Each is counted once.`,
  applyInParts: (count: number) => `Apply it as ${count} parts in order. Each part after the first reads a fresh dry run and stops if anything changed other than the records earlier parts created.`,
  continueFrom: (index: number, count: number) => `Continue from part ${index + 1} of ${count}`,
  continueEffect: (index: number, count: number, applied: number) => `Continue from part ${index + 1} of ${count}. ${plural(applied, "part is", "parts are")} already applied and never sent again.`,
  partWrote: (index: number, count: number, rows: number) => `${part(index, count)} · ${plural(rows, "row", "rows")} recorded with ${rows === 1 ? "its receipt" : "their receipts"}.`,
  partRefusedRows: (rows: number) => plural(rows, "row refused", "rows refused"),
  partRow: (index: number, count: number, sentence: string) => `${part(index, count)} · ${sentence}`,
  partStopped: (index: number, count: number, sentence: string) => `${part(index, count)} not applied. ${sentence}`,
  partsApplied: (applied: number, count: number) => `${applied} of ${count} parts applied`,
  drift: (changes: Array<{ key: string; before: Verdict | null; after: Verdict | null }>) => {
    const shown = changes.slice(0, 3).map((c) => `${c.key}: reviewed as ${verdict(c.before)}, now ${verdict(c.after)}`).join("; ");
    const more = changes.length > 3 ? `; and ${plural(changes.length - 3, "more", "more")}` : "";
    return `It changed since your review: ${shown}${more}. Read the dry run again, then continue.`;
  },
};
